import React, { useEffect } from "react";
import "./style.scss";
import { Link } from "react-router-dom";
import {
  scrollToTop,
  cxo_hero,
  cxo_hero_mob,
  cxo_logo,
  cxo_marketing,
  cxo_engineering,
  cxo_cloud,
  cxo_finance,
  cxo_hubspot,
  cxo_postman,
  cxo_github,
  cxo_gcloud,
  cxo_azure,
  cxo_zoho,
} from "src/shared/utils/appConstant";
import { pageRoutes } from "src/shared/utils/pageRoutes.constant";
import HomePageFooter from "src/views/home/components/Footer";
import Footer from "src/components/footer";

// Inline AWS Logo Component
const AwsLogo: React.FC<{ className?: string }> = ({ className = "system-logo" }) => (
  <svg
    viewBox="0 0 75 45"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={{ height: "38px", width: "auto" }}
  >
    <text
      x="2"
      y="24"
      fontFamily="'Open Sans', sans-serif"
      fontSize="24"
      fontWeight="800"
      fill="#232F3E"
    >
      aws
    </text>
    <path
      d="M5 33 C22 41 52 38 68 28 M60 27 L69 28 L64 35"
      stroke="#FF9900"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CXOTulz: React.FC = () => {
  useEffect(() => {
    scrollToTop();
  }, []);

  const insightCards = [
    {
      id: "marketing",
      title: "Marketing",
      desc: "Understand contact growth, engagement, and lifecycle movement.",
      icon: cxo_marketing,
    },
    {
      id: "engineering",
      title: "Engineering",
      desc: "Get visibility into repositories, contributors, PR activity, and engineering signals.",
      icon: cxo_engineering,
    },
    {
      id: "cloud",
      title: "Cloud",
      desc: "Monitor cloud costs, resources, usage trends, and security-related insights.",
      icon: cxo_cloud,
    },
    {
      id: "finance",
      title: "Finance",
      desc: "View revenue, expenses, profitability, and overall financial health.",
      icon: cxo_finance,
    },
  ];

  const roleCards = [
    {
      num: "1.",
      title: "CXO Group Members",
      desc: "Gain rapid access to crucial cloud cost and security data, facilitating informed decision-making.",
    },
    {
      num: "2.",
      title: "Cloud Administrators",
      desc: "Simplify cloud resource management, ensuring cost-effectiveness and security measures are upheld.",
    },
    {
      num: "3.",
      title: "IT Managers",
      desc: "Enhance IT infrastructure operations with actionable insights, reducing risks and maximizing efficiency.",
    },
    {
      num: "4.",
      title: "Data Analysts",
      desc: "Extract valuable insights from cloud data to drive strategic resource allocation and decision-making.",
    },
    {
      num: "5.",
      title: "Cloud Consultants",
      desc: "Seamlessly implement and execute cloud management strategies to optimize organizational performance.",
    },
    {
      num: "6.",
      title: "Enterprise Users",
      desc: "Streamline operations, minimize costs, and fortify security measures to drive organizational growth.",
    },
  ];

  const systemCards = [
    {
      id: "hubspot",
      logos: [
        <img key="hubspot" src={cxo_hubspot} alt="HubSpot" className="system-logo" />,
      ],
      title: "Contacts, Engagement & Lifecycle",
      desc: "Direct connection to your HubSpot portal for growth, workflow, and campaign efficiency.",
    },
    {
      id: "postman-github",
      logos: [
        <img key="postman" src={cxo_postman} alt="Postman" className="system-logo" />,
        <img key="github" src={cxo_github} alt="GitHub" className="system-logo" />,
      ],
      title: "Repositories, Contributors & API Insights",
      desc: "Consolidated signals of developer velocity, pull requests, and endpoint reliability.",
    },
    {
      id: "clouds",
      logos: [
        <img key="gcloud" src={cxo_gcloud} alt="Google Cloud" className="system-logo" />,
        <img key="azure" src={cxo_azure} alt="Azure" className="system-logo" />,
        <AwsLogo key="aws" />,
      ],
      title: "Costs, Resources & Security",
      desc: "Multi-tenant cloud insights showing aggregate spending and primary platform health metrics.",
    },
    {
      id: "zoho",
      logos: [
        <img key="zoho" src={cxo_zoho} alt="Zoho" className="system-logo" />,
      ],
      title: "Revenue, Expenses & Profitability",
      desc: "Unified view of cash flow, operating expenses, and direct ledger insight from accounting.",
    },
  ];

  const leadershipQuestions = [
    "What is changing?",
    "Where are costs increasing?",
    "Where are risks emerging?",
    "What is driving business performance?",
  ];

  return (
    <div className="cxotulz-container animate__animated animate__fadeIn">
      {/* 1. Hero Section */}
      <section className="cxotulz-hero-section">
        <div className="hero-backdrop-pillar" />
        <div className="cxotulz-hero-content">
          <div className="hero-left-block animate__animated animate__fadeInLeft">
            <div className="hero-text-content">
              <h1 className="cxotulz-hero-title">CXOTulz</h1>
              <p className="cxotulz-hero-desc">
                CXOTulz brings key business, technology, cloud, and financial insights
                into one executive view — helping leadership understand what is happening
                across the organization without switching between multiple systems.
              </p>
            </div>
          </div>
          <div className="hero-right-logo animate__animated animate__fadeInRight">
            <img src={cxo_logo} alt="CXOTulz Logo" className="cxotulz-logo-img" />
          </div>
        </div>
        <div className="cxotulz-breadcrumb">
          <Link to="/" className="breadcrumb-link">
            Home
          </Link>
          <span className="breadcrumb-sep">/</span>
          <Link to={pageRoutes.Marketplace} className="breadcrumb-link">
            Marketplace
          </Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">CXOTulz</span>
        </div>
      </section>

      {/* 2. "What is CXOTulz?" Section */}
      <section className="what-is-cxotulz-section">
        <div className="what-is-cxotulz-grid">
          <div className="what-is-text-col animate__animated animate__fadeInUp">
            <div className="what-is-header-row">
              <h2 className="what-is-outline-title">
                What is
                <br />
                <span className="cxotulz-outline-row">
                  CXOTulz?
                  <span className="mobile-dots" aria-hidden="true">
                    <span className="dot dot-red" />
                    <span className="dot dot-blue" />
                    <span className="dot dot-red" />
                  </span>
                </span>
              </h2>
            </div>

            {/* In mobile, mockup is displayed between title and description */}
            <div className="what-is-mockup-mobile">
              <object data={cxo_hero_mob} type="image/svg+xml" aria-label="CXOTulz Dashboard Overview" className="cxotulz-hero-mockup-img" style={{ pointerEvents: 'none' }} />
            </div>

            <p className="what-is-p1">
              Modern businesses operate across multiple platforms.{" "}
              <a
                href="https://cxotulz.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="highlight-blue-link"
              >
                <strong className="highlight-blue">CXOTulz</strong>
              </a>{" "}
              brings the most
              relevant information from these systems into a unified view designed for
              leadership.
            </p>
            <p className="what-is-p2">
              Instead of navigating individual operational dashboards, CXOs get a clearer
              picture of performance, costs, risks, and business outcomes.
            </p>
          </div>

          <div className="what-is-mockup-col animate__animated animate__fadeInRight">
            <img
              src={cxo_hero}
              alt="CXOTulz Dashboard Overview"
              className="cxotulz-hero-mockup-img"
            />
          </div>
        </div>
      </section>

      {/* 3. "CXOTulz brings together business, tech, and financial insights." Section */}
      <section className="cxotulz-insights-section">
        <div className="section-header">
          <h2 className="section-title">
            CXOTulz brings together <span className="highlight-blue">business</span>,{" "}
            <span className="highlight-blue">tech</span>, and{" "}
            <span className="highlight-blue">financial</span> insights.
          </h2>
        </div>

        <div className="insights-grid-container">
          <div className="insights-grid">
            {insightCards.map((card) => (
              <div className={`insight-card insight-${card.id}`} key={card.id}>
                <div className="insight-icon-wrap">
                  <img src={card.icon} alt={card.title} className="insight-icon-img" />
                </div>
                <h3 className="insight-title">{card.title}</h3>
                <p className="insight-desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. "Unlock seamless Management for every role" Section */}
      <section className="cxotulz-roles-section">
        <div className="section-header">
          <h2 className="section-title">
            Unlock seamless <span className="highlight-blue">Management</span> for every
            role
          </h2>
          <p className="section-subtitle">
            Anyone who seeks to manage and optimize their cloud operations can benefit
            from using CXOTulz. This includes:
          </p>
        </div>

        <div className="roles-grid">
          {roleCards.map((card, idx) => (
            <div className="role-card" key={idx}>
              <div className="role-num">{card.num}</div>
              <h3 className="role-card-title">{card.title}</h3>
              <p className="role-card-desc">{card.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. "From Different Systems to One Executive View" Section */}
      <section className="cxotulz-systems-section">
        <div className="section-header">
          <h2 className="section-title">
            From Different Systems to{" "}
            <span className="highlight-blue">One Executive View</span>
          </h2>
        </div>

        <div className="systems-grid">
          {systemCards.map((card) => (
            <div className="system-card" key={card.id}>
              <div className="system-logos-row">
                {card.logos.map((logo, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span className="logo-plus-sep">+</span>}
                    {logo}
                  </React.Fragment>
                ))}
              </div>
              <h3 className="system-card-title">{card.title}</h3>
              <p className="system-card-desc">{card.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 6. "Everything Leadership Needs. In One View." Section */}
      <section className="cxotulz-leadership-section">
        <div className="leadership-content">
          <div className="leadership-left-col">
            <h2 className="leadership-title">
              Everything Leadership Needs.
              <br />
              <span className="highlight-blue">In One View.</span>
            </h2>
            <p className="leadership-desc">
              CXOTulz turns information from different systems into an executive-level
              dashboard. Instead of presenting raw operational data, the platform
              highlights the information leadership needs to understand:
            </p>
          </div>

          <div className="leadership-right-col">
            <div className="questions-list">
              {leadershipQuestions.map((q, idx) => (
                <div className="question-item" key={idx}>
                  <span className="question-bullet">•</span>
                  <span className="question-text">{q}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pre-Footer & Footer */}
      <HomePageFooter page="Marketplace" />
      <Footer />
    </div>
  );
};

export default CXOTulz;
